"use client";

import Link from "next/link";
import { Menu, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { navigation } from "@/lib/data";
import { SiteLogo } from "@/components/ui/SiteLogo";
import { Button } from "@/components/ui/Button";

export function Header() {
  const [isOpen, setIsOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const firstLinkRef = useRef<HTMLAnchorElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setIsOpen(false);
    };
    document.addEventListener("keydown", onKey);
    firstLinkRef.current?.focus();
    return () => document.removeEventListener("keydown", onKey);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) buttonRef.current?.focus();
  }, [isOpen]);

  return (
    <header className="sticky top-0 z-50 border-b border-white/10 bg-canvas/70 backdrop-blur-xl">
      <div className="shell">
        <div className="flex min-h-20 items-center justify-between gap-6">
          {/* VOR-58: imagotype reduzido na navbar — ~180px width = ~29px height
              (mantém aspect 790:128 do SVG horizontal) */}
          <SiteLogo priority width={180} />

          {/* VOR-54: nav inline em desktop (md+), hamburger só em mobile/tablet pequeno */}
          <nav
            aria-label="Navegação principal"
            className="hidden items-center gap-1 md:flex"
          >
            {navigation.map((item) => (
              <Link
                key={item.href}
                href={item.href}
                className="rounded-full px-3 py-2 text-sm text-mist transition hover:bg-white/5 hover:text-white focus-visible:ring-2 focus-visible:ring-accent"
              >
                {item.label}
              </Link>
            ))}
            <Button href="/contato" className="ml-2 px-5 py-2.5">
              Entrar em contato
            </Button>
          </nav>

          {/* Hamburger só em < md */}
          <button
            ref={buttonRef}
            type="button"
            className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-white/10 bg-white/5 text-white transition hover:bg-white/10 focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-canvas md:hidden"
            aria-expanded={isOpen}
            aria-controls="primary-menu"
            aria-label={isOpen ? "Fechar menu" : "Abrir menu"}
            onClick={() => setIsOpen((current) => !current)}
          >
            {isOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>

        {isOpen ? (
          <>
            <div
              aria-hidden="true"
              className="fixed inset-0 z-40 bg-canvas/60 backdrop-blur-sm md:hidden"
              onClick={() => setIsOpen(false)}
            />
            <nav
              id="primary-menu"
              aria-label="Navegação principal (mobile)"
              className="panel relative z-50 mb-4 mt-2 flex flex-col gap-2 p-4 sm:p-6 md:hidden"
            >
              {navigation.map((item, index) => (
                <Link
                  key={item.href}
                  ref={index === 0 ? firstLinkRef : undefined}
                  href={item.href}
                  className="rounded-2xl px-4 py-3 text-base text-mist transition hover:bg-white/5 hover:text-white focus-visible:ring-2 focus-visible:ring-accent"
                  onClick={() => setIsOpen(false)}
                >
                  {item.label}
                </Link>
              ))}
              {/* VOR-145: CTA mobile consome o Button do DS; fecha o menu no clique. */}
              <Button
                href="/contato"
                className="mt-2 px-5 py-3"
                onClick={() => setIsOpen(false)}
              >
                Entrar em contato
              </Button>
            </nav>
          </>
        ) : null}
      </div>
    </header>
  );
}
